import type { CraftRoomResponse, MaterialResponse, CraftProjectDisplayResponse, CraftRoomProjectListItem } from '@mrb91002/shared-types'
import React, { createContext, useContext, useState, useEffect } from 'react'

import { fetchCraftRoomPage, fetchSecureCraftRoomPage } from '../api/fetchers'

import { useAuth } from './AuthContext'

// package-local — project type union for context (public API returns CraftRoomProjectListItem, secure API returns CraftProjectDisplayResponse)
type ContextProjectType = CraftProjectDisplayResponse | CraftRoomProjectListItem

interface CraftRoomContextType {
  craftRoom: CraftRoomResponse | null
  materials: MaterialResponse[]
  projects: ContextProjectType[]
  publicProjects: ContextProjectType[]
  privateProjects: ContextProjectType[]
  archivedProjects: ContextProjectType[]
  loading: boolean
  error: string | null
  refetchCraftRoom: () => Promise<void>
  hasMaterial: (materialId: string) => boolean
  getUserMaterialBySource: (sourceMaterialId: string) => MaterialResponse | undefined
  updateMaterials: (updater: (materials: MaterialResponse[]) => MaterialResponse[]) => void
}

const CraftRoomContext = createContext<CraftRoomContextType | undefined>(undefined)

export const CraftRoomProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, screen_name, idToken } = useAuth()
  const [craftRoom, setCraftRoom] = useState<CraftRoomResponse | null>(null)
  const [materials, setMaterials] = useState<MaterialResponse[]>([])
  const [projects, setProjects] = useState<ContextProjectType[]>([])
  const [publicProjects, setPublicProjects] = useState<ContextProjectType[]>([])
  const [privateProjects, setPrivateProjects] = useState<ContextProjectType[]>([])
  const [archivedProjects, setArchivedProjects] = useState<ContextProjectType[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchCraftRoom = async () => {
    if (!user || !screen_name) return

    try {
      setLoading(true)
      setError(null)

      // Use secure API for authenticated users viewing their own craft room
      if (idToken) {
        const secureData = await fetchSecureCraftRoomPage(screen_name, idToken)
        setCraftRoom(secureData.craftRoom)
        setMaterials(secureData.materials || [])
        
        // Keep projects separated
        const publicProjs = secureData.public_projects || []
        const privateProjs = secureData.private_projects || []
        const archivedProjs = secureData.archived_projects || []
        
        setPublicProjects(publicProjs)
        setPrivateProjects(privateProjs)
        setArchivedProjects(archivedProjs)
        
        // Combined array for backward compatibility
        setProjects([...publicProjs, ...privateProjs, ...archivedProjs])
      } else {
        // Fallback to public API if no token
        const pageData = await fetchCraftRoomPage(screen_name)
        setCraftRoom(pageData.craftRoom)
        setMaterials(pageData.craftRoom.materials || [])
        
        const publicProjs = pageData.projects || []
        setPublicProjects(publicProjs)
        setPrivateProjects([])
        setArchivedProjects([])
        setProjects(publicProjs)
      }
    } catch (err: unknown) {
      console.error('Failed to load craft room:', err)
      setError(err instanceof Error ? err.message : 'Failed to load craft room')
    } finally {
      setLoading(false)
    }
  }

  const refetchCraftRoom = async () => {
    await fetchCraftRoom()
  }

  const hasMaterial = (materialId: string): boolean => {
    return materials.some(
      (m) => m.material_id === materialId || m.public_material_id === materialId || m.source_material_id === materialId,
    )
  }

  const getUserMaterialBySource = (sourceMaterialId: string): MaterialResponse | undefined => {
    return materials.find((m) => m.source_material_id === sourceMaterialId || m.public_material_id === sourceMaterialId)
  }

  const updateMaterials = (updater: (materials: MaterialResponse[]) => MaterialResponse[]) => {
    setMaterials(updater)
  }

  // Clear data when user logs out
  useEffect(() => {
    if (!user) {
      setCraftRoom(null)
      setMaterials([])
      setProjects([])
      setPublicProjects([])
      setPrivateProjects([])
      setArchivedProjects([])
      setError(null)
    }
  }, [user])

  return (
    <CraftRoomContext.Provider
      value={{
        craftRoom,
        materials,
        projects,
        publicProjects,
        privateProjects,
        archivedProjects,
        loading,
        error,
        refetchCraftRoom,
        hasMaterial,
        getUserMaterialBySource,
        updateMaterials,
      }}
    >
      {children}
    </CraftRoomContext.Provider>
  )
}

export const useCraftRoom = () => {
  const context = useContext(CraftRoomContext)
  if (context === undefined) {
    throw new Error('useCraftRoom must be used within a CraftRoomProvider')
  }
  return context
}
